import { Button } from '@toggle/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useNavigateToThemeOverview } from '~/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';

import { MarketCatalystWidget } from './market-catalyst-widget/MarketCatalystWidget';
import * as S from './MarketCatalystDrawer.styles';
import { useMarketCatalystDrawer } from './useMarketCatalystDrawer';

export const MarketCatalystDrawer = () => {
  const { t } = useTranslation(['insights']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackCatalystDrawerOpen, trackThemeOpen } = usePosthogEvents();

  const [expandCitations, setExpandCitations] = useState(false);

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const { closeDrawer, isOpen, themeId, drawerType, insightId, utmSource } =
    useMarketCatalystDrawer();

  useEffect(() => {
    if (isOpen && utmSource === POSTHOG_TRACKING_SOURCES.EMAIL) {
      trackCatalystDrawerOpen(utmSource, 'market');
    }
  }, [isOpen, utmSource]);

  const handleHeaderButtonClick = () => {
    if (themeId) {
      trackThemeOpen(POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER);
      navigateToTheme({ themeId });
    }

    return null;
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: themeId,
        expandAccordions: setExpandCitations,
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <S.StyledDrawer
      data-testid="market-catalyst-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <S.HeaderButtons>
          {drawerType === 'thematic' && (
            <Button
              variant="empty"
              label={t('insights:catalyst.viewOnTheme')}
              iconName="Expand"
              size="xsmall"
              onClick={handleHeaderButtonClick}
            />
          )}
          <DownloadButton onClick={handleDownload} />
        </S.HeaderButtons>
      }
    >
      <S.ContentWrapper ref={contentWrapperRef}>
        <ErrorBoundaryWrapper>
          {!!insightId && (
            <MarketCatalystWidget
              insightId={insightId}
              expandCitations={expandCitations}
            />
          )}
        </ErrorBoundaryWrapper>
        <ScheduleDemoBanner />
      </S.ContentWrapper>
    </S.StyledDrawer>
  );
};
